import React, { useEffect, useState, useRef } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ClockIcon, CalendarClockIcon } from 'lucide-react';
interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}
export function DateTimePicker({
  value,
  onChange,
  label
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date(value || new Date()));
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDate = value ? new Date(value) : new Date();
  const hours = selectedDate.getHours();
  const minutes = selectedDate.getMinutes();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({
    start: monthStart,
    end: monthEnd
  });
  const startDay = monthStart.getDay();
  const paddedDays = Array(startDay).fill(null).concat(days);
  const handleDateSelect = (day: Date) => {
    const newDate = new Date(day);
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    onChange(newDate.toISOString());
  };
  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const newDate = new Date(selectedDate);
    newDate.setHours(newHours);
    newDate.setMinutes(newMinutes);
    onChange(newDate.toISOString());
  };
  return <div className="w-full" ref={containerRef} data-id="element-1227">
      {label && <div className="flex items-center gap-2 mb-2" data-id="element-1228">
          <CalendarClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FC4C02]" data-id="element-1229" />
          <span className="font-bold text-[#2D2D2D] text-sm" data-id="element-1230">{label}</span>
        </div>}
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between h-12 px-4 bg-white border border-[#E8E8ED] rounded-xl text-left hover:border-[#D0D0D8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FC4C02]" data-id="element-1231">
        <div className="flex items-center gap-3" data-id="element-1232">
          <CalendarIcon className="h-4 w-4 text-[#9B9BA7]" data-id="element-1233" />
          <span className="text-[#2D2D2D] font-medium" data-id="element-1234">
            {format(selectedDate, 'MMM dd, yyyy')}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[#6B6B76]" data-id="element-1235">
          <ClockIcon className="h-4 w-4" data-id="element-1236" />
          <span className="font-mono" data-id="element-1237">{format(selectedDate, 'HH:mm')}</span>
        </div>
      </button>

      {isOpen && <div className="absolute z-50 mt-2 bg-white rounded-2xl border border-[#E8E8ED] shadow-xl p-4 w-[320px]" data-id="element-1238">
          <div className="flex items-center justify-between mb-4" data-id="element-1239">
            <button type="button" onClick={() => setViewDate(subMonths(viewDate, 1))} className="p-2 hover:bg-[#F7F7FA] rounded-lg transition-colors" data-id="element-1240">
              <ChevronLeftIcon className="h-4 w-4 text-[#6B6B76]" data-id="element-1241" />
            </button>
            <span className="text-[#2D2D2D] font-semibold" data-id="element-1242">
              {format(viewDate, 'MMMM yyyy')}
            </span>
            <button type="button" onClick={() => setViewDate(addMonths(viewDate, 1))} className="p-2 hover:bg-[#F7F7FA] rounded-lg transition-colors" data-id="element-1243">
              <ChevronRightIcon className="h-4 w-4 text-[#6B6B76]" data-id="element-1244" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-1245">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-center text-xs font-medium text-[#9B9BA7] py-1" data-id="element-1246">
                {day}
              </div>)}
          </div>

          <div className="grid grid-cols-7 gap-1 mb-4" data-id="element-1247">
            {paddedDays.map((day, i) => <button key={i} type="button" disabled={!day} onClick={() => day && handleDateSelect(day)} className={`h-9 w-9 rounded-lg text-sm font-medium transition-colors
                  ${!day ? 'invisible' : ''}
                  ${day && isSameDay(day, selectedDate) ? 'bg-[#FC4C02] text-white' : ''}
                  ${day && !isSameDay(day, selectedDate) && isSameMonth(day, viewDate) ? 'text-[#2D2D2D] hover:bg-[#F7F7FA]' : ''}
                  ${day && !isSameMonth(day, viewDate) ? 'text-[#9B9BA7]' : ''}
                `} data-id="element-1248">
                {day ? format(day, 'd') : ''}
              </button>)}
          </div>

          <div className="border-t border-[#E8E8ED] pt-4" data-id="element-1249">
            <div className="flex items-center justify-center gap-2" data-id="element-1250">
              <input type="number" min="0" max="23" value={hours.toString().padStart(2, '0')} onChange={e => handleTimeChange(parseInt(e.target.value) || 0, minutes)} className="w-14 h-10 bg-[#F7F7FA] border border-[#E8E8ED] rounded-lg text-center text-[#2D2D2D] font-mono text-lg focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent" data-id="element-1251" />
              <span className="text-[#9B9BA7] text-xl font-bold" data-id="element-1252">:</span>
              <input type="number" min="0" max="59" value={minutes.toString().padStart(2, '0')} onChange={e => handleTimeChange(hours, parseInt(e.target.value) || 0)} className="w-14 h-10 bg-[#F7F7FA] border border-[#E8E8ED] rounded-lg text-center text-[#2D2D2D] font-mono text-lg focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent" data-id="element-1253" />
            </div>
          </div>

          <div className="flex justify-between mt-4 pt-4 border-t border-[#E8E8ED]" data-id="element-1254">
            <button type="button" onClick={() => {
          const t = new Date();
          t.setHours(hours);
          t.setMinutes(minutes);
          onChange(t.toISOString());
          setViewDate(t);
        }} className="text-sm text-[#FC4C02] font-medium hover:text-[#E04400]" data-id="element-1255">
              Today
            </button>
            <button type="button" onClick={() => setIsOpen(false)} className="text-sm bg-[#FC4C02] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#E04400]" data-id="element-1256">
              Done
            </button>
          </div>
        </div>}
    </div>;
}